import Link from "next/link"
import { useTranslation } from "react-i18next"
import styled from "styled-components"
import { theme } from "@project/shared"
import { useReform202606AlertVisibility } from "./useReform202606AlertVisibility"

// 報酬改定202606 セーフティ1 (01-F-05、adult ADULT-293 相当)
// 設計書: easyy-docs/reforms/202606/kids/設計/共通_データ移行方針.md §セーフティ1
//
// 目的:
//   2026/6 月の処遇改善加算が未設定または旧コードのまま残っている事業所に対し、
//   画面アラートで再設定を促す。セーフティ2 (請求時エラー) が発動する前に事業所が
//   気付けるようにするための UI ガード。
//
// 判定ロジックは useReform202606AlertVisibility.ts に集約済み。表示期間・条件の詳細は
// そちらのコメント参照。バナー表示は確定判定 (isVisible) のみ参照し、loading/error 時は
// 偽陽性アラート防止のため非表示 (サイレントフェイル)。
//
// クリーンアップ予定: 2027/4/1 以降に別 PR で削除。

const AlertBanner = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  padding: 16px 24px;
  margin-bottom: 24px;
  border: 2px solid ${theme.red2};
  border-radius: 12px;
  background: ${theme.red5};
  color: ${theme.red2};

  .message {
    flex: 1;

    .heading {
      font-weight: bold;
      font-size: 16px;
      margin-bottom: 4px;
    }

    .body {
      font-size: 14px;
      color: ${theme.black};
    }
  }

  .action a {
    display: inline-block;
    padding: 8px 20px;
    background: ${theme.red2};
    color: white;
    border-radius: 4px;
    text-decoration: none;
    font-weight: bold;
    white-space: nowrap;
  }

  .action a:hover {
    opacity: 0.85;
  }

  @media screen and (max-width: ${theme.breakpoints.md}) {
    flex-direction: column;
    align-items: stretch;

    .action {
      text-align: right;
    }
  }
`

type Props = {
  facilityId: number | string
}

export const Reform202606Alert = ({ facilityId }: Props) => {
  const { t } = useTranslation()
  // バナー表示は確定判定 (isVisible) のみ参照。loading/error 時は非表示。
  const { isVisible } = useReform202606AlertVisibility(facilityId)

  if (!isVisible) return null

  return (
    <AlertBanner>
      <div className={"message"}>
        <div className={"heading"}>
          {t(
            "[Important] Re-registration of the wage improvement addition for June 2026 is required"
          )}
        </div>
        <div className={"body"}>
          {t(
            "Please switch to the new categories from the system status management screen for June 2026 before processing the June 2026 billing."
          )}
        </div>
      </div>
      <div className={"action"}>
        <Link href={`/facility/system-status/${facilityId}?year=2026&month=6`}>
          {t("Set up now")}
        </Link>
      </div>
    </AlertBanner>
  )
}
